<template>
  <div class="overflow-y-auto p-4">
    <el-card
      class="rounded-lg border bg-card text-card-foreground shadow-2xs mb-6"
    >
      <div class="flex flex-col space-y-1.5 p-6">
        <h3 class="text-2xl font-semibold leading-none tracking-tight">
          Filters &amp; Search
        </h3>
      </div>
      <el-form label-position="top">
        <div class="p-6 pt-0">
          <div class="grid gap-4 md:grid-cols-6">
            <div class="md:col-span-2">
              <div class="flex flex-col gap-2">
                <label
                  class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Agent Name</label
                >
                <DebounceInput
                  placeholder="Search agent name"
                  v-model="filterAgentName"
                  clearable
                >
                  <template #prepend>
                    <i class="el-icon-search"></i>
                  </template>
                </DebounceInput>
              </div>
            </div>
            <div class="md:col-span-2">
              <div class="flex flex-col gap-2">
                <label
                  class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Model Name
                </label>
                <DebounceInput
                  placeholder="Search model name"
                  v-model="filterModelName"
                  clearable
                >
                  <template #prepend>
                    <i class="el-icon-search"></i>
                  </template>
                </DebounceInput>
              </div>
            </div>
            <div class="flex flex-col gap-2">
              <label
                class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                AI Agent
              </label>
              <SmartSelect
                v-model="tableCtrl.queryParameters.agent_id"
                :add-all-option="{ key: undefined, value: 'All Agents' }"
                :load-method="getAgents"
                value-field="agent_id"
                display-field="name"
              />
            </div>
            <div class="flex flex-col gap-2">
              <label
                class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Model
              </label>
              <SmartSelect
                v-model="tableCtrl.queryParameters.base_model"
                :options="enumToOptions(AIAgentModel)"
                :add-all-option="{ value: 'All Models', key: undefined }"
                clearable
                placeholder="Model"
              />
            </div>
          </div>
        </div>
      </el-form>
    </el-card>
    <el-card class="rounded-lg border bg-card text-card-foreground shadow-2xs">
      <div class="flex flex-row justify-between items-center py-4">
        <div></div>
        <div>
          <el-button
            v-if="false"
            type="primary"
            icon="el-icon-plus"
            @click="handleAddModel()"
          >
            Model
          </el-button>
        </div>
      </div>
      <div class="p-0">
        <div class="relative w-full overflow-auto">
          <el-table
            v-loading="tableCtrl.state.loading"
            element-loading-background="rgba(24, 33, 48, 0.8)"
            :data="tableCtrl.state.tableData"
            style="width: 100%"
            :resizable="false"
          >
            <el-table-column prop="name" label="Agent">
              <template slot-scope="scope">
                <div class="flex flex-row items-center">
                  <el-avatar
                    :src="scope.row.avatar_url"
                    size="small"
                    class="mr-2"
                  />
                  <el-tooltip :content="scope.row.name" placement="top">
                    <span
                      class="flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
                    >
                      {{ scope.row.name }}
                    </span>
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="" label="Model">
              <template slot-scope="scope">
                <el-tooltip
                  :content="scope.row.config?.modelName"
                  placement="top"
                >
                  <span
                    class="flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
                  >
                    {{ scope.row.config?.modelName || "N/A" }}
                  </span>
                </el-tooltip>
              </template>
            </el-table-column>
            <el-table-column prop="status" label="Status">
              <template slot-scope>
                <span>active</span>
              </template>
            </el-table-column>
            <el-table-column
              label="Actions"
              width="180"
              align="right"
              fixed="right"
            >
              <template slot-scope="scope">
                <ActionButtons
                  :actions="actions"
                  :item="scope.row"
                  @command="handleCommand"
                />
              </template>
            </el-table-column>
          </el-table>
          <div class="flex mt-4 justify-end">
            <el-pagination
              @size-change="(val) => tableCtrl.setPageSize(val)"
              @current-change="(val) => tableCtrl.setCurrentPage(val)"
              :current-page="tableCtrl.state.pagination.page"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="tableCtrl.state.pagination.pageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="tableCtrl.state.pagination.total"
            />
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import SmartSelect from "@/components/SmartControls/SmartSelect.vue";
import ActionButtons from "@/components/ActionButtons.vue";
import { TableController } from "@/controllers/TableController";
import { delegateLoadTable } from "@/controllers/TableControllerUtils";
import BotSvg from "!!raw-loader!@/assets/bot.svg";
import BrainSvg from "!!raw-loader!@/assets/brain.svg";
import {
  getAiAuditList,
  getAgents,
  deleteWebsocketShadowInstance,
  deleteAgent,
} from "@/api";
import {
  EventLogLevel,
  EventLogStatus,
  enumValueToLabel,
  enumToOptions,
  AIAgentType,
  AIAgentModel,
} from "@/enums";

export default {
  name: "AiAuditLogsAgentList",
  components: {
    SmartSelect,
    ActionButtons,
  },
  emits: ["tabIndex"],
  data() {
    return {
      BotSvg,
      BrainSvg,
      tabIndex: 1,
      tableCtrl: new TableController({
        loadMethod: (opt) =>
          delegateLoadTable(
            this.$store,
            "agentStore/fetchAgents",
            "agentStore/agents",
            opt
          ),
        autoLoad: true,
        isClientPaged: true,
        pageSize: 10,
        keyField: "agent_id",
        queryParameters: {
          name: "",
          "config.type": AIAgentType.ALERT,
          "config.modelName": "",
        },
      }),
    };
  },
  computed: {
    AIAgentModel: () => AIAgentModel,
    filterAgentName: {
      get() {
        return this.tableCtrl.queryParameters.name?.replace(/%/g, "");
      },
      set(value) {
        this.tableCtrl.queryParameters.name = `%${value}%`;
      },
    },
    filterModelName: {
      get() {
        return this.tableCtrl.queryParameters["config.modelName"]?.replace(
          /%/g,
          ""
        );
      },
      set(value) {
        this.tableCtrl.queryParameters["config.modelName"] = `%${value}%`;
      },
    },
    actions: () => [
      {
        type: "button",
        label: "Chat",
        command: "chat",
        icon: "el-icon-message",
      },
      {
        type: "dropdown",
        label: "more",
        icon: "el-icon-more",
        items: [
          {
            label: "Edit",
            command: "edit",
            icon: "el-icon-edit",
          },
          {
            label: "Delete",
            command: "delete",
            icon: "el-icon-delete",
            color: "#F56C6C",
            divided: true,
          },
        ],
      },
    ],
    EventLogLevel: () => EventLogLevel,
    EventLogStatus: () => EventLogStatus,
  },
  watch: {
    "tableCtrl.queryParameters": {
      deep: true,
      handler() {
        this.tableCtrl.filterData();
      },
    },
  },
  methods: {
    enumToOptions,
    enumValueToLabel,
    getAgents,
    async handleRefresh() {
      await this.tableCtrl.refresh();
    },
    async fetchData(params) {
      const res = await getAiAuditList(params);
      return res;
    },
    updateValue(value) {
      this.emit("input", value);
    },
    async handleAddModel() {
      const me = this;
      await me.handleEditModel(null, "create");
      me.$message.success("Model created successfully");
      await me.handleRefresh();
    },
    async handleCommand({ command, item }) {
      const me = this;
      if (["view", "edit", "feedback"].includes(command)) {
        await me.handleEditModel(item, command);
      } else if (command === "chat") {
        await me.openChatDialog(item);
      } else if (command === "delete") {
        await me.handleDelete(item);
      }
      me.$message.success("Operation successful");
      await me.handleRefresh();
    },
    async handleDelete(item) {
      const me = this;
      await me.$confirm(
        `Are you sure you want to delete ${item?.audit?.feedback}?`,
        "Warning",
        {
          type: "warning",
        }
      );

      if (item.websocket) {
        await deleteWebsocketShadowInstance(item.websocket);
      }

      try {
        me.loading = true;
        await deleteAgent(item.agent_id);
        me.$message({
          type: "success",
          message: `Deleted model "${item.name}" successfully!`,
        });
      } finally {
        me.loading = false;
      }
    },
    async handleEditModel(item, command) {
      const me = this;
      // 首字母大写处理
      const cap = command
        ? command.charAt(0).toUpperCase() + command.slice(1)
        : "Edit";
      await me.$loaderWindow({
        title: `${cap} Model`,
        component: () => import("./EditAiModelDialog.vue"),
        closeOnClickModal: false,
        closeOnPressEscape: false,
        props: {
          value: item,
          defaultAgentPrompt: {
            config: {
              type: AIAgentType.ALERT,
            },
          },
        },
      });

      me.$message({
        type: "success",
        message: `${item ? "Edited" : "Created"} Model successfully!`,
      });
      // 项id是服务器生成的，重新加载数据
      await me.handleRefresh();
    },
    handleChangeTab(index) {
      this.$emit("tabIndex", index);
    },
    async openChatDialog(item) {
      await this.$loaderWindow({
        title: `Chat with ${item.name}`,
        component: () => import("@/pages/ai-agent/components/ChatDialog.vue"),
        height: "80%",
        width: "70%",
        showHeader: false,
        padding: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        props: {
          agent: {
            agent_id: item.agent_id,
          },
        },
      });
    },
  },
};
</script>
